import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { companyInfo } from '../mock';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Message Sent!', {
      description: "Thank you for contacting us. We'll get back to you soon.",
      style: {
        background: 'rgba(30, 64, 98, 0.95)',
        border: '1px solid #00a8ff',
        color: '#00a8ff',
        boxShadow: '0 0 20px rgba(0, 168, 255, 0.5)'
      }
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container mx-auto px-6">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-12 animate-slide-up">
          <div className="w-20 h-20 bg-[rgba(0,168,255,0.2)] rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ boxShadow: '0 0 30px rgba(0,168,255,0.5)' }}>
            <Mail className="text-[#00a8ff]" size={40} style={{ filter: 'drop-shadow(0 0 10px #00a8ff)' }} />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold led-text animate-led-pulse mb-6" style={{ textShadow: '0 0 20px #00a8ff, 0 0 40px #4cd3ff' }}>
            GET IN TOUCH
          </h1>
          <p className="text-xl text-[#00a8ff]" style={{ textShadow: '0 0 10px #00a8ff' }}>
            Let's discuss how we can help transform your business
          </p>
        </div>

        {/* Contact Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="animate-slide-up">
            <h2 className="text-3xl font-bold led-text mb-8" style={{ textShadow: '0 0 15px #00a8ff' }}>CONTACT INFORMATION</h2>
            
            <div className="space-y-6 mb-12">
              <Card className="bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl hover:shadow-[0_0_30px_#00a8ff] transition-all duration-300" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}>
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[rgba(0,168,255,0.2)] rounded-lg flex items-center justify-center flex-shrink-0" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.5)' }}>
                    <Mail className="text-[#00a8ff]" size={24} />
                  </div>
                  <div>
                    <div className="font-bold text-white mb-1 led-text" style={{ textShadow: '0 0 8px #00a8ff' }}>Email</div>
                    <a href={`mailto:${companyInfo.email}`} className="text-[#00a8ff] hover:text-[#4cd3ff] transition-colors" style={{ textShadow: '0 0 5px #00a8ff' }}>
                      {companyInfo.email}
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl hover:shadow-[0_0_30px_#00a8ff] transition-all duration-300" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}>
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[rgba(0,168,255,0.2)] rounded-lg flex items-center justify-center flex-shrink-0" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.5)' }}>
                    <Phone className="text-[#00a8ff]" size={24} />
                  </div>
                  <div>
                    <div className="font-bold text-white mb-1 led-text" style={{ textShadow: '0 0 8px #00a8ff' }}>Phone</div>
                    <a href={`tel:${companyInfo.phone}`} className="text-[#00a8ff] hover:text-[#4cd3ff] transition-colors" style={{ textShadow: '0 0 5px #00a8ff' }}>
                      {companyInfo.phone}
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl hover:shadow-[0_0_30px_#00a8ff] transition-all duration-300" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}>
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[rgba(0,168,255,0.2)] rounded-lg flex items-center justify-center flex-shrink-0" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.5)' }}>
                    <MapPin className="text-[#00a8ff]" size={24} />
                  </div>
                  <div>
                    <div className="font-bold text-white mb-1 led-text" style={{ textShadow: '0 0 8px #00a8ff' }}>Office</div>
                    <p className="text-[#00a8ff]" style={{ textShadow: '0 0 5px #00a8ff' }}>{companyInfo.address}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Office Hours */}
            <Card className="bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.3)' }}>
              <CardContent className="p-6">
                <h3 className="font-bold text-white mb-4 led-text" style={{ textShadow: '0 0 8px #00a8ff' }}>OFFICE HOURS</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#00a8ff]">Monday - Friday:</span>
                    <span className="font-bold text-white">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#00a8ff]">Saturday:</span>
                    <span className="font-bold text-white">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#00a8ff]">Sunday:</span>
                    <span className="font-bold text-white">Closed</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="animate-slide-up">
            <Card className="bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl" style={{ boxShadow: '0 0 30px rgba(0,168,255,0.3)' }}>
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold led-text mb-8" style={{ textShadow: '0 0 15px #00a8ff' }}>SEND US A MESSAGE</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-[#00a8ff] font-bold mb-2 block">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff]"
                      placeholder="Your full name"
                      style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-[#00a8ff] font-bold mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff]"
                      placeholder="your.email@example.com"
                      style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-[#00a8ff] font-bold mb-2 block">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff]"
                      placeholder="+1 (555) 123-4567"
                      style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-[#00a8ff] font-bold mb-2 block">Subject *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff]"
                      placeholder="How can we help?"
                      style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#00a8ff] font-bold mb-2 block">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff] resize-none"
                      rows={6}
                      placeholder="Tell us about your project..."
                      style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#00a8ff] text-[#0a2540] hover:bg-[#4cd3ff] font-bold py-6 transition-all duration-300 hover:scale-105"
                    style={{ boxShadow: '0 0 20px #00a8ff' }}
                  >
                    Send Message
                    <Send className="ml-2" size={18} />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
